/**
 * Detailed Report Page - Comprehensive VC Analysis Report
 * Shows advanced analytics, Bayesian validation, Monte Carlo projections
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  TrendingUp, ArrowLeft, Loader2, Brain, BarChart3, Target,
  AlertTriangle, CheckCircle, Users, DollarSign, Shield, Zap,
  Clock, Award, TrendingDown, Eye, FileDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

interface DetailedReport {
  company_name: string;
  analysis_id: string;
  overall_recommendation: string;
  overall_score: number;
  confidence_score: number;
  generated_at: string;
  investment_thesis: string;
  analyst_results: AnalystResult[];
  bayesian_validations: BayesianValidation[];
  monte_carlo_projections: MonteCarloProjection[];
  action_items: ActionItem[];
  risk_factors: RiskFactor[];
  comparable_companies: ComparableCompany[];
}

interface AnalystResult {
  domain: string;
  score: number;
  confidence: number;
  key_findings: string[];
  risks: string[];
  recommendations: string[];
  benchmarks: Record<string, any>;
  processing_time_ms: number;
}

interface BayesianValidation {
  claim: string;
  likelihood_score: number;
  confidence_interval: [number, number];
  supporting_evidence: string[];
  contradicting_evidence: string[];
}

interface MonteCarloProjection {
  metric: string;
  mean_value: number;
  percentile_10: number;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_90: number;
  simulations_run: number;
}

interface ActionItem {
  priority: number;
  domain: string;
  action: string;
  timeline: string;
  owner: string;
  success_criteria: string;
}

interface RiskFactor {
  domain: string;
  risk: string;
  impact: string;
  likelihood: string;
  mitigation_priority: string;
}

interface ComparableCompany {
  company: string;
  stage: string;
  valuation: string;
  revenue_multiple: string;
  similarity_score: number;
  key_differences: string[];
}

const domainIcons: Record<string, React.ReactNode> = {
  'Market Analysis': <Target className="w-5 h-5" />,
  'Financial Analysis': <DollarSign className="w-5 h-5" />,
  'Technical Analysis': <Zap className="w-5 h-5" />,
  'Go-to-Market Analysis': <TrendingUp className="w-5 h-5" />,
  'Team & Governance Analysis': <Users className="w-5 h-5" />,
  'ESG & Regulatory Analysis': <Shield className="w-5 h-5" />
};

const recommendationColors: Record<string, string> = {
  'STRONG_YES': 'text-green-700 bg-green-50 border-green-200',
  'YES': 'text-blue-700 bg-blue-50 border-blue-200',
  'YES_IF_CONDITIONS_MET': 'text-yellow-700 bg-yellow-50 border-yellow-200',
  'PASS_WITH_FEEDBACK': 'text-orange-700 bg-orange-50 border-orange-200',
  'HARD_PASS': 'text-red-700 bg-red-50 border-red-200'
};

const formatMetricLabel = (label: string): string =>
  label
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const DetailedReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<DetailedReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadReport = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const response = await api.get(`/analyses/${id}/detailed-report`);
        setReport(response.data);
      } catch (error: any) {
        console.error('Failed to load detailed report:', error);
        toast.error('Failed to load detailed report');
      } finally {
        setIsLoading(false);
      }
    };

    loadReport();
  }, [id]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-600">Generating comprehensive analysis...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-gray-600">Report not found</p>
          <Link to="/analyses" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            ← Back to analyses
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const totalAnalysts = report.analyst_results.length;
  const analystDomainLabels = report.analyst_results.map((result) => result.domain);
  const monteCarloSimulations = report.monte_carlo_projections.reduce(
    (max, projection) => Math.max(max, projection.simulations_run),
    0
  );
  const confidenceRange = report.analyst_results.length
    ? report.analyst_results.reduce(
        (range, result) => ({
          min: Math.min(range.min, result.confidence),
          max: Math.max(range.max, result.confidence)
        }),
        { min: report.analyst_results[0].confidence, max: report.analyst_results[0].confidence }
      )
    : { min: report.confidence_score, max: report.confidence_score };
  const averageConfidence = report.analyst_results.length
    ? report.analyst_results.reduce((sum, result) => sum + result.confidence, 0) / totalAnalysts
    : report.confidence_score;
  const processingRange = report.analyst_results.length
    ? report.analyst_results.reduce(
        (range, result) => ({
          min: Math.min(range.min, result.processing_time_ms),
          max: Math.max(range.max, result.processing_time_ms)
        }),
        {
          min: report.analyst_results[0].processing_time_ms,
          max: report.analyst_results[0].processing_time_ms
        }
      )
    : { min: 0, max: 0 };
  const tierSequence = ['STRONG_YES', 'YES', 'YES_IF_CONDITIONS_MET', 'PASS_WITH_FEEDBACK', 'HARD_PASS'];

  // Print-to-PDF via the browser's native print dialog. Server-side
  // PDF generation is not wired yet; the print stylesheet at the
  // bottom of this component hides chrome and produces a clean,
  // letter-paginated capture that users can "Save as PDF".
  const handleDownloadPdf = () => {
    window.print();
  };

  return (
    <DashboardLayout>
      {/* Print-to-PDF stylesheet. Activated by the Download PDF button
          (which calls window.print()). Hides the dashboard sidebar /
          header / interactive chrome so the printed output is just the
          report content. */}
      <style>{`
        @media print {
          nav, aside, header, .no-print, [data-no-print] { display: none !important; }
          body { background: #fff !important; }
          .max-w-7xl { max-width: 100% !important; padding: 0 !important; }
          .bg-white, .bg-blue-50, .bg-gray-50 { box-shadow: none !important; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/analyses"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{report.company_name}</h1>
              <p className="text-gray-600 mt-1">Comprehensive Due Diligence Report</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-3 md:space-y-0 print:hidden">
            <button
              onClick={handleDownloadPdf}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Download PDF
            </button>
            <div className={`px-4 py-2 rounded-lg border font-semibold ${recommendationColors[report.overall_recommendation] || 'text-gray-700 bg-gray-50 border-gray-200'}`}>
              {report.overall_recommendation.replace(/_/g, ' ')}
            </div>
          </div>
        </div>

        {/* Framework Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Brain className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Advanced Analytics Framework</h2>
            </div>
            <ul className="space-y-3 text-sm text-gray-700">
              {report.bayesian_validations.length > 0 && (
                <li>
                  <span className="font-semibold text-gray-900">Bayesian Claim Validation:</span>
                  {' '}Uses statistical methods to assess claim likelihood with confidence intervals across {report.bayesian_validations.length} priority claims.
                </li>
              )}
              <li>
                <span className="font-semibold text-gray-900">Monte Carlo Financial Modeling:</span>
                {' '}Runs {monteCarloSimulations ? monteCarloSimulations.toLocaleString() : '10,000+'} simulations for revenue and IRR projections with full percentile analysis.
              </li>
              <li>
                <span className="font-semibold text-gray-900">Multi-Criteria Decision Analysis:</span>
                {' '}Weighted scoring across {totalAnalysts} expert domains with risk adjustments producing an overall {report.overall_score.toFixed(1)}/100 score.
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Enhanced Expert Analysis System</h2>
            </div>
            <ul className="space-y-3 text-sm text-gray-700">
              <li>
                <span className="font-semibold text-gray-900">6 Specialized Analysts:</span>
                {' '}Market, Financial, Technical, GTM, Team/Governance, ESG/Regulatory ({analystDomainLabels.join(', ')}).
              </li>
              <li>
                <span className="font-semibold text-gray-900">Parallel Processing:</span>
                {' '}All expert assessments execute concurrently with completion windows between {processingRange.min}ms and {processingRange.max}ms.
              </li>
              <li>
                <span className="font-semibold text-gray-900">Confidence Scoring:</span>
                {' '}Risk-weighted confidences range {confidenceRange.min.toFixed(0)}%–{confidenceRange.max.toFixed(0)}% (avg {averageConfidence.toFixed(0)}%) and directly influence the final weighting.
              </li>
              <li>
                <span className="font-semibold text-gray-900">Benchmarking Integration:</span>
                {' '}Real-time peer comparisons with percentile rankings embedded in every domain benchmark.
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Target className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Sophisticated Decision Engine</h2>
            </div>
            <ul className="space-y-3 text-sm text-gray-700">
              <li>
                <span className="font-semibold text-gray-900">5-Tier Recommendations:</span>
                {' '}
                {tierSequence.map((tier, index) => (
                  <span
                    key={tier}
                    className={`${tier === report.overall_recommendation ? 'font-semibold text-blue-700' : 'text-gray-700'}`}
                  >
                    {tier.replace(/_/g, ' ')}
                    {index < tierSequence.length - 1 ? ' → ' : ''}
                  </span>
                ))}
                <span className="text-gray-500"> (current tier highlighted)</span>
                .
              </li>
              <li>
                <span className="font-semibold text-gray-900">Risk-Adjusted Scoring:</span>
                {' '}Incorporates uncertainty via {report.confidence_score.toFixed(0)}% confidence weighting and Bayesian intervals.
              </li>
              <li>
                <span className="font-semibold text-gray-900">Actionable Gap Analysis:</span>
                {' '}{report.action_items.length} prioritized actions with timelines and clear owners.
              </li>
              <li>
                <span className="font-semibold text-gray-900">Investment Thesis Generation:</span>
                {' '}Auto-generated rationale summarizing the core investment case.
              </li>
            </ul>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Brain className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Executive Summary</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">{report.overall_score.toFixed(1)}</div>
              <div className="text-sm text-gray-600">Overall Score</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">{report.confidence_score.toFixed(0)}%</div>
              <div className="text-sm text-gray-600">Confidence Level</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">{report.analyst_results.length}</div>
              <div className="text-sm text-gray-600">Expert Analysts</div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Investment Thesis</h3>
            <p className="text-blue-800 leading-relaxed">{report.investment_thesis}</p>
          </div>
        </div>

        {/* Analyst Results */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Expert Analysis Results</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {report.analyst_results.map((result, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {domainIcons[result.domain]}
                    <h3 className="font-semibold text-gray-900">{result.domain}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{result.score.toFixed(1)}</div>
                    <div className="text-xs text-gray-500">{result.confidence.toFixed(0)}% confidence</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-green-700 mb-1">Key Findings</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {result.key_findings.map((finding, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-red-700 mb-1">Risk Factors</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {result.risks.map((risk, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{risk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {result.recommendations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-blue-700 mb-1">Recommended Actions</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {result.recommendations.map((recommendation, i) => (
                          <li key={i} className="flex items-start space-x-2">
                            <CheckCircle className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{recommendation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.benchmarks && Object.keys(result.benchmarks).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-purple-700 mb-1">Benchmarking</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600">
                        {Object.entries(result.benchmarks).map(([metric, value]) => (
                          <div key={metric} className="flex items-center justify-between bg-purple-50 rounded px-2 py-1">
                            <span className="text-purple-900 font-medium">{formatMetricLabel(metric)}</span>
                            <span className="text-purple-700">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-xs text-gray-500 flex justify-between">
                  <span>Processing time: {result.processing_time_ms} ms</span>
                  <span>Confidence weight: {result.confidence.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bayesian Validations — hidden when empty (the pipeline does
            not yet populate priority claims, so an empty section was
            misleading). Renders only when validations are present. */}
        {report.bayesian_validations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Eye className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Bayesian Claim Validation</h2>
          </div>

          <div className="space-y-4">
            {report.bayesian_validations.map((validation, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{validation.claim}</h3>
                  <div className="text-right">
                    <div className="text-lg font-bold text-purple-600">{(validation.likelihood_score * 100).toFixed(0)}%</div>
                    <div className="text-xs text-gray-500">Likelihood</div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 mb-3">
                  Confidence interval: {validation.confidence_interval[0].toFixed(1)} – {validation.confidence_interval[1].toFixed(1)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-green-700 mb-2">Supporting Evidence</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {validation.supporting_evidence.map((evidence, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{evidence}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-red-700 mb-2">Contradicting Evidence</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {validation.contradicting_evidence.map((evidence, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{evidence}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Monte Carlo Projections */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Monte Carlo Financial Projections</h2>
            <span className="text-sm text-gray-500">({report.monte_carlo_projections[0]?.simulations_run.toLocaleString()} simulations)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {report.monte_carlo_projections.map((projection, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-4">{projection.metric}</h3>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Mean Value:</span>
                    <span className="font-semibold">{projection.mean_value.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">10th Percentile:</span>
                    <span className="text-red-600">{projection.percentile_10.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">25th Percentile:</span>
                    <span className="text-orange-600">{projection.percentile_25.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Median:</span>
                    <span className="font-semibold">{projection.percentile_50.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">75th Percentile:</span>
                    <span className="text-blue-600">{projection.percentile_75.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">90th Percentile:</span>
                    <span className="text-green-600">{projection.percentile_90.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Items & Risk Factors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Action Items */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Clock className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Action Items</h2>
            </div>

            <div className="space-y-3">
              {report.action_items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                        P{item.priority}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{item.domain}</span>
                    </div>
                    <span className="text-xs text-gray-500">{item.timeline}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{item.action}</p>
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Owner:</span> {item.owner}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    <span className="font-medium">Success Criteria:</span> {item.success_criteria}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Factors */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">Risk Factors</h2>
            </div>

            <div className="space-y-3">
              {report.risk_factors.map((risk, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                      risk.mitigation_priority === 'P1' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {risk.mitigation_priority}
                    </span>
                    <span className="text-sm font-medium text-gray-700">{risk.domain}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{risk.risk}</p>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span><strong>Impact:</strong> {risk.impact}</span>
                    <span><strong>Likelihood:</strong> {risk.likelihood}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparable Companies */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Award className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Comparable Companies</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {report.comparable_companies.map((company, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{company.company}</h3>
                  <span className="text-sm text-blue-600 font-medium">
                    {(company.similarity_score * 100).toFixed(0)}% match
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stage:</span>
                    <span className="font-medium">{company.stage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Valuation:</span>
                    <span className="font-medium">{company.valuation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Revenue Multiple:</span>
                    <span className="font-medium">{company.revenue_multiple}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <h4 className="text-xs font-medium text-gray-700 mb-1">Key Differences:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {company.key_differences.map((diff, i) => (
                      <li key={i}>• {diff}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 py-4">
          Report generated on {new Date(report.generated_at).toLocaleString()} • Analysis ID: {report.analysis_id}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DetailedReportPage;
